import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, FileText, ClipboardList, Truck, Receipt, Package, ListTree,
  UserRound, DollarSign, RefreshCcw, Search, X, ExternalLink, Link2Off,
  Building2, Percent, ScrollText, AlertCircle, ChevronLeft, ChevronRight,
} from "lucide-react";

const ENTITIES = [
  { key: "customer",  label: "Customers",   icon: Users,         group: "People" },
  { key: "vendor",    label: "Vendors",     icon: Truck,         group: "People" },
  { key: "employee",  label: "Employees",   icon: UserRound,     group: "People" },
  { key: "invoice",   label: "Invoices",    icon: FileText,      group: "Sales" },
  { key: "estimate",  label: "Estimates",   icon: ClipboardList, group: "Sales" },
  { key: "payment",   label: "Payments",    icon: DollarSign,    group: "Sales" },
  { key: "creditmemo",label: "Credit Memos",icon: FileText,      group: "Sales" },
  { key: "bill",      label: "Bills",       icon: Receipt,       group: "Expenses" },
  { key: "purchase",  label: "Expenses",    icon: Receipt,       group: "Expenses" },
  { key: "item",      label: "Items",       icon: Package,       group: "Catalog" },
  { key: "account",   label: "Chart of Accounts", icon: ListTree,group: "Catalog" },
  { key: "tax-summary", label: "Tax & CRA", icon: AlertCircle,   group: "Tax" },
  { key: "taxagency", label: "Tax Agencies",icon: Building2,     group: "Tax" },
  { key: "taxcode",   label: "Tax Codes",   icon: ScrollText,    group: "Tax" },
  { key: "taxrate",   label: "Tax Rates",   icon: Percent,       group: "Tax" },
];

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const GROUPS = ["People", "Sales", "Expenses", "Catalog", "Tax"];
const PAGE_SIZE = 1000;

const QuickBooksTab = () => {
  const [entity, setEntity] = useState("customer");
  const [rows, setRows] = useState(null);
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(null); // full QBO row count (null = unknown)
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [taxSummary, setTaxSummary] = useState(null);

  const load = async (nextEntity = entity, nextQ = q, nextPage = 1) => {
    setBusy(true);
    setErr("");
    setNotConnected(false);
    try {
      if (nextEntity === "tax-summary") {
        const r = await api.get(`/qbo/tax-summary`);
        setTaxSummary(r.data);
        setRows([]);
        setCount(0);
        setTotal(null);
      } else {
        const r = await api.get(`/qbo/browse/${nextEntity}`, {
          params: { q: nextQ, limit: PAGE_SIZE, start: (nextPage - 1) * PAGE_SIZE + 1 },
        });
        setRows(r.data?.rows || []);
        setCount(r.data?.count || 0);
        setTotal(typeof r.data?.total === "number" ? r.data.total : null);
        setPage(nextPage);
        setTaxSummary(null);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detailMsg = e?.response?.data?.detail || "Failed to load QuickBooks data";
      if (status === 400 && detailMsg.toLowerCase().includes("not connected")) {
        setNotConnected(true);
      } else {
        setErr(detailMsg);
        toast.error(detailMsg);
      }
      setRows([]);
      setCount(0);
      setTotal(null);
      setTaxSummary(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load(entity, "");
    setQ("");
  }, [entity]);

  const openDetail = async (row) => {
    setDetail({ loading: true, entity, row });
    try {
      const r = await api.get(`/qbo/browse/${entity}/${row.id}`);
      setDetail({ loading: false, entity, row: r.data });
    } catch {
      setDetail({ loading: false, entity, row });
    }
  };

  const grouped = useMemo(() => {
    const byGroup = {};
    GROUPS.forEach((g) => (byGroup[g] = []));
    ENTITIES.forEach((e) => byGroup[e.group].push(e));
    return byGroup;
  }, []);

  const activeEntity = ENTITIES.find((e) => e.key === entity);

  // Pager math
  const totalPages = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const pageNums = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const keep = new Set([1, 2, page - 1, page, page + 1, totalPages - 1, totalPages].filter((n) => n >= 1 && n <= totalPages));
    return [...keep].sort((a, b) => a - b);
  }, [page, totalPages]);
  const rangeFrom = count ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeTo = (page - 1) * PAGE_SIZE + count;

  return (
    <div className="flex h-[calc(100vh-8rem)] overflow-hidden bg-[#f4f5f8]" data-testid="quickbooks-tab">
      {/* Sidebar — entity picker (scrolls independently of the main list) */}
      <aside className="hidden h-full w-56 shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white md:flex">
        <div className="shrink-0 border-b border-gray-200 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-600">Read-only</p>
          <h2 className="text-sm font-bold uppercase tracking-tight text-gray-800">QuickBooks Browser</h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="qbo-sidebar-scroll">
          {GROUPS.map((g) => (
            <div key={g} className="px-2 py-2">
              <p className="px-2 font-mono text-[10px] uppercase tracking-widest text-gray-400">{g}</p>
              <ul className="mt-1 space-y-0.5">
                {grouped[g].map(({ key, label, icon: Icon }) => (
                  <li key={key}>
                    <button
                      onClick={() => setEntity(key)}
                      data-testid={`qbo-entity-${key}`}
                      className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors ${
                        entity === key
                          ? "bg-emerald-50 text-emerald-700"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="flex-1 truncate">{label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </aside>

      {/* Main list (scrolls independently of the sidebar) */}
      <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            {activeEntity && <activeEntity.icon className="h-4 w-4 text-emerald-600" />}
            <h3 className="text-sm font-semibold text-gray-800">{activeEntity?.label}</h3>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="qbo-count-label">
              {entity === "tax-summary"
                ? (taxSummary ? "current position" : "…")
                : (rows ? (total != null && total > 0 ? `${rangeFrom}–${rangeTo} of ${total}` : `${count} shown`) : "…")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile entity picker */}
            <select
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              data-testid="qbo-entity-select"
              className="rounded border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 md:hidden"
            >
              {ENTITIES.map((e) => (
                <option key={e.key} value={e.key}>{e.label}</option>
              ))}
            </select>
            {entity !== "tax-summary" && (
              <>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load(entity, q, 1)}
                  placeholder="Search…"
                  data-testid="qbo-search-input"
                  className="w-40 rounded border border-gray-200 px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-emerald-400 sm:w-56"
                />
                <button
                  onClick={() => load(entity, q, 1)}
                  disabled={busy}
                  data-testid="qbo-search-btn"
                  className="inline-flex items-center gap-1 rounded bg-emerald-500 px-2.5 py-1.5 text-xs font-semibold text-black hover:bg-emerald-400 disabled:opacity-50"
                >
                  <Search className="h-3 w-3" />
                  Search
                </button>
              </>
            )}
            <button
              onClick={() => load(entity, q, page)}
              disabled={busy}
              data-testid="qbo-refresh-btn"
              className="rounded border border-gray-200 bg-white p-1.5 text-gray-600 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="qbo-list-scroll">
          {notConnected ? (
            <div className="p-8 text-center">
              <Link2Off className="mx-auto h-8 w-8 text-amber-500" />
              <p className="mt-3 text-sm font-semibold text-gray-800">QuickBooks isn&apos;t connected yet</p>
              <p className="mt-1 text-xs text-gray-500">
                Ask a superadmin to open Settings → API and click Connect QuickBooks.
              </p>
            </div>
          ) : err ? (
            <p className="p-8 text-center text-sm text-red-600">{err}</p>
          ) : entity === "tax-summary" ? (
            taxSummary === null && busy ? (
              <p className="p-8 text-center text-sm text-gray-400">Crunching your tax position…</p>
            ) : taxSummary ? (
              <TaxSummaryPanel data={taxSummary} onOpenTxn={(t) => setDetail({ loading: false, entity: t.kind, row: t })} />
            ) : (
              <p className="p-8 text-center text-sm text-gray-400">No data.</p>
            )
          ) : rows === null ? (
            <p className="p-8 text-center text-sm text-gray-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-400">
              No {activeEntity?.label.toLowerCase()} match your search.
            </p>
          ) : (
            <QboRowList entity={entity} rows={rows} onSelect={openDetail} />
          )}
        </div>

        {/* Pager — 1000 rows per page, arrows + page numbers for the rest */}
        {entity !== "tax-summary" && total != null && totalPages > 1 && (
          <div
            className="flex shrink-0 flex-wrap items-center justify-center gap-1 border-t border-gray-200 bg-white px-3 py-2"
            data-testid="qbo-pager"
          >
            <button
              onClick={() => load(entity, q, page - 1)}
              disabled={busy || page <= 1}
              data-testid="qbo-page-prev"
              aria-label="Previous page"
              className="rounded border border-gray-200 bg-white p-1.5 text-gray-600 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {pageNums.map((n, i) => (
              <span key={n} className="flex items-center">
                {i > 0 && pageNums[i - 1] < n - 1 && <span className="px-1 text-xs text-gray-400">…</span>}
                <button
                  onClick={() => load(entity, q, n)}
                  disabled={busy}
                  data-testid={`qbo-page-${n}`}
                  className={`min-w-8 rounded px-2 py-1.5 text-xs font-semibold ${
                    n === page
                      ? "bg-emerald-500 text-black"
                      : "border border-gray-200 bg-white text-gray-600 hover:border-emerald-400 hover:text-emerald-700"
                  } disabled:opacity-40`}
                >
                  {n}
                </button>
              </span>
            ))}
            <button
              onClick={() => load(entity, q, page + 1)}
              disabled={busy || page >= totalPages}
              data-testid="qbo-page-next"
              aria-label="Next page"
              className="rounded border border-gray-200 bg-white p-1.5 text-gray-600 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </main>

      {detail && (
        <QboDetailModal
          entity={detail.entity}
          row={detail.row}
          loading={detail.loading}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

// -------------------- List --------------------
const QboRowList = ({ entity, rows, onSelect }) => {
  if (["customer", "vendor", "employee"].includes(entity)) {
    return (
      <ul>
        {rows.map((r) => (
          <li key={r.id} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => onSelect(r)}
              data-testid={`qbo-row-${r.id}`}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-800">{r.name || `#${r.id}`}</p>
                <p className="truncate text-[11px] text-gray-400">
                  {r.email || r.phone || r.company || `QBO ID ${r.id}`}
                </p>
              </div>
              {typeof r.balance === "number" && r.balance !== 0 && (
                <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${
                  r.balance > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                }`}>
                  {money(r.balance)}
                </span>
              )}
              {r.active === false && (
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-gray-500">Inactive</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    );
  }
  if (entity === "item") {
    return (
      <ul>
        {rows.map((r) => (
          <li key={r.id} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => onSelect(r)}
              data-testid={`qbo-row-${r.id}`}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-800">{r.name}</p>
                <p className="truncate text-[11px] text-gray-400">
                  {r.type || "Item"}{r.sku ? ` · SKU ${r.sku}` : ""}
                </p>
              </div>
              {typeof r.unit_price === "number" && (
                <p className="shrink-0 font-mono text-sm font-semibold text-gray-800">{money(r.unit_price)}</p>
              )}
            </button>
          </li>
        ))}
      </ul>
    );
  }
  if (entity === "account") {
    return (
      <ul>
        {rows.map((r) => (
          <li key={r.id} className="border-b border-gray-100 last:border-b-0">
            <button
              onClick={() => onSelect(r)}
              data-testid={`qbo-row-${r.id}`}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-800">{r.name}</p>
                <p className="truncate text-[11px] text-gray-400">
                  {r.type}{r.subtype ? ` · ${r.subtype}` : ""}
                </p>
              </div>
              {typeof r.balance === "number" && (
                <p className="shrink-0 font-mono text-sm font-semibold text-gray-800">{money(r.balance)}</p>
              )}
            </button>
          </li>
        ))}
      </ul>
    );
  }
  // txn-like: invoice / estimate / bill / purchase / payment / creditmemo
  return (
    <ul>
      {rows.map((r) => (
        <li key={r.id} className="border-b border-gray-100 last:border-b-0">
          <button
            onClick={() => onSelect(r)}
            data-testid={`qbo-row-${r.id}`}
            className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] font-semibold text-gray-700">
                #{r.doc_number || r.id}
                {r.customer && <span className="ml-2 font-sans text-gray-500">{r.customer}</span>}
                {r.vendor && !r.customer && <span className="ml-2 font-sans text-gray-500">{r.vendor}</span>}
              </p>
              <p className="text-[11px] text-gray-400">
                {r.txn_date || ""}
                {r.due_date ? ` · due ${r.due_date}` : ""}
                {r.expiration_date ? ` · exp ${r.expiration_date}` : ""}
                {r.status ? ` · ${r.status}` : ""}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-mono text-sm font-semibold text-gray-800">{money(r.total)}</p>
              {Number(r.balance) > 0 ? (
                <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-600">
                  {money(r.balance)} due
                </p>
              ) : Number(r.total) > 0 ? (
                <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Paid</p>
              ) : null}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

// -------------------- Detail modal --------------------
const qboAddr = (a) => {
  if (!a) return null;
  const seen = new Set();
  const parts = [a.Line1, a.Line2, a.Line3, a.Line4, [a.City, a.CountrySubDivisionCode].filter(Boolean).join(", "), a.PostalCode]
    .map((p) => String(p || "").trim())
    .filter((p) => {
      const k = p.toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return parts.length ? parts : null;
};

const QboDetailModal = ({ entity, row, loading, onClose }) => {
  const raw = row?._raw || row || {};
  const line = row || {};
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-3"
      onClick={onClose}
      data-testid="qbo-detail-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-600">
              QuickBooks {entity}
            </p>
            <h3 className="truncate text-lg font-bold uppercase tracking-tight text-gray-800">
              {line.name || (line.doc_number ? `#${line.doc_number}` : `ID ${line.id}`)}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="qbo-detail-close"
            className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-4">
          {loading ? (
            <p className="text-center text-sm text-gray-400">Loading full record…</p>
          ) : (
            <>
              {/* Customer profile — clean fields from the QBO record */}
              {entity === "customer" && (
                <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="qbo-customer-profile">
                  {raw.CompanyName && raw.CompanyName !== raw.DisplayName && (
                    <div className="rounded bg-gray-50 px-3 py-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Company</p>
                      <p className="truncate text-sm font-semibold text-gray-800">{raw.CompanyName}</p>
                    </div>
                  )}
                  {raw.PrimaryEmailAddr?.Address && (
                    <div className="rounded bg-gray-50 px-3 py-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Email</p>
                      <p className="truncate text-sm font-semibold text-gray-800" data-testid="qbo-customer-email">{raw.PrimaryEmailAddr.Address}</p>
                    </div>
                  )}
                  {raw.PrimaryPhone?.FreeFormNumber && (
                    <div className="rounded bg-gray-50 px-3 py-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Phone</p>
                      <p className="text-sm font-semibold text-gray-800" data-testid="qbo-customer-phone">{raw.PrimaryPhone.FreeFormNumber}</p>
                    </div>
                  )}
                  {typeof raw.Balance === "number" && raw.Balance !== 0 && (
                    <div className="rounded bg-amber-50 px-3 py-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-amber-700">Balance</p>
                      <p className="font-mono text-sm font-semibold text-amber-800">{money(raw.Balance)}</p>
                    </div>
                  )}
                  {qboAddr(raw.BillAddr) && (
                    <div className="rounded bg-gray-50 px-3 py-2 sm:col-span-2" data-testid="qbo-customer-address">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Billing address</p>
                      {qboAddr(raw.BillAddr).map((l, i) => (
                        <p key={i} className="text-sm text-gray-800">{l}</p>
                      ))}
                    </div>
                  )}
                  {qboAddr(raw.ShipAddr) && (
                    <div className="rounded bg-gray-50 px-3 py-2 sm:col-span-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Shipping address</p>
                      {qboAddr(raw.ShipAddr).map((l, i) => (
                        <p key={i} className="text-sm text-gray-800">{l}</p>
                      ))}
                    </div>
                  )}
                  {raw.Notes && (
                    <div className="rounded bg-gray-50 px-3 py-2 sm:col-span-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Notes</p>
                      <p className="text-sm text-gray-800">{raw.Notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Summary strip */}
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {["txn_date", "due_date", "expiration_date"].map((k) => (
                  line[k] ? (
                    <div key={k} className="rounded bg-gray-50 px-3 py-2">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">{k.replace("_", " ")}</p>
                      <p className="font-mono text-sm font-semibold text-gray-800">{line[k]}</p>
                    </div>
                  ) : null
                ))}
                {typeof line.total === "number" && (
                  <div className="rounded bg-gray-50 px-3 py-2">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Total</p>
                    <p className="font-mono text-sm font-semibold text-gray-800">{money(line.total)}</p>
                  </div>
                )}
                {typeof line.balance === "number" && line.balance !== 0 && (
                  <div className="rounded bg-amber-50 px-3 py-2">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-amber-700">Balance due</p>
                    <p className="font-mono text-sm font-semibold text-amber-800">{money(line.balance)}</p>
                  </div>
                )}
                {(line.customer || line.vendor) && (
                  <div className="rounded bg-gray-50 px-3 py-2 sm:col-span-2">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">
                      {line.customer ? "Customer" : "Vendor"}
                    </p>
                    <p className="text-sm font-semibold text-gray-800">{line.customer || line.vendor}</p>
                  </div>
                )}
              </div>

              {/* Line items if present */}
              {Array.isArray(raw.Line) && raw.Line.length > 0 && (
                <div className="mb-4 rounded border border-gray-200">
                  <p className="border-b border-gray-100 bg-gray-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                    Line items
                  </p>
                  <ul>
                    {raw.Line.filter((l) => l.DetailType !== "SubTotalLineDetail").map((l, i) => {
                      const detail = l.SalesItemLineDetail || l.ItemBasedExpenseLineDetail || l.AccountBasedExpenseLineDetail || {};
                      const itemName = (detail.ItemRef || detail.AccountRef || {}).name;
                      return (
                        <li key={i} className="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0">
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-gray-800">{itemName || l.Description || `Line ${i + 1}`}</p>
                            {l.Description && itemName && (
                              <p className="truncate text-[11px] text-gray-500">{l.Description}</p>
                            )}
                            {detail.Qty && (
                              <p className="font-mono text-[10px] text-gray-400">
                                Qty {detail.Qty} @ {money(detail.UnitPrice)}
                              </p>
                            )}
                          </div>
                          <p className="shrink-0 font-mono text-sm font-semibold text-gray-800">{money(l.Amount)}</p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Notes */}
              {line.memo && (
                <div className="mb-3 rounded border border-gray-200 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Customer memo</p>
                  <p className="mt-1 text-sm text-gray-700">{line.memo}</p>
                </div>
              )}
              {line.private_note && (
                <div className="mb-3 rounded border border-gray-200 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Private note</p>
                  <p className="mt-1 text-sm text-gray-700">{line.private_note}</p>
                </div>
              )}

              {/* Raw JSON toggle */}
              <details className="rounded border border-gray-200">
                <summary className="cursor-pointer bg-gray-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  View raw QuickBooks JSON
                </summary>
                <pre className="max-h-80 overflow-auto p-3 font-mono text-[10px] leading-relaxed text-gray-600">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Read-only — view only</p>
          <a
            href={`https://qbo.intuit.com/app/${entity === "customer" ? "customerdetail" : entity === "vendor" ? "vendordetail" : "salestransactions"}?nameId=${line.id || ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
          >
            Open in QuickBooks <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default QuickBooksTab;

// -------------------- Tax & CRA summary panel --------------------
const TaxSummaryPanel = ({ data, onOpenTxn }) => {
  const t = data?.totals || {};
  const accts = data?.tax_accounts || [];
  const txns = data?.cra_transactions || [];
  const agencies = data?.tax_agencies || [];

  return (
    <div className="space-y-4 p-4">
      {/* Big three number strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-red-700">Total owed to CRA</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${Number(t.total_owed_to_cra) > 0 ? "text-red-900" : "text-emerald-900"}`}>
            {money(t.total_owed_to_cra)}
          </p>
          <p className="mt-1 text-[11px] text-red-700/70">
            {Number(t.total_owed_to_cra) > 0
              ? "Account balances + unpaid CRA bills"
              : "You are net-owed / prepaid"}
          </p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-700">From tax accounts</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${Number(t.owed_from_accounts) > 0 ? "text-amber-900" : "text-emerald-900"}`}>
            {money(t.owed_from_accounts)}
          </p>
          <p className="mt-1 text-[11px] text-amber-700/70">HST + payroll + income tax accumulated but not yet remitted</p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-700">
            YTD paid to CRA {t.ytd_year ? `(${t.ytd_year})` : ""}
          </p>
          <p className="mt-1 font-mono text-2xl font-bold text-emerald-900">{money(t.ytd_paid_to_cra)}</p>
          <p className="mt-1 text-[11px] text-emerald-700/70">All CRA bills + purchases settled this year</p>
        </div>
      </div>

      {Number(t.owed_from_cra_bills) > 0 && (
        <div className="theme-keep-bg rounded-lg border border-red-300 bg-red-100 px-4 py-2 text-sm text-red-800">
          <span className="font-semibold">{money(t.owed_from_cra_bills)}</span> in CRA bills is still unpaid on your AP —
          scroll down to see the individual bills.
        </div>
      )}

      {/* Tax-payable accounts */}
      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
          <h4 className="text-sm font-semibold text-gray-800">Tax-payable accounts</h4>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{accts.length}</span>
        </div>
        {accts.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-400">
            No tax-payable accounts detected in your Chart of Accounts.
          </p>
        ) : (
          <ul>
            {accts.map((a) => {
              const owed = Number(a.owed || 0);
              return (
                <li key={a.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-800">{a.name}</p>
                    <p className="truncate text-[11px] text-gray-400">
                      {a.type}{a.subtype ? ` · ${a.subtype}` : ""}
                      {a.active === false ? " · Inactive" : ""}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`font-mono text-sm font-semibold ${
                      owed > 0 ? "text-red-700" : owed < 0 ? "text-emerald-700" : "text-gray-500"
                    }`}>
                      {money(owed)}
                    </p>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400">
                      {owed > 0 ? "you owe" : owed < 0 ? "prepaid / credit" : "clear"}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* CRA remittances */}
      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
          <h4 className="text-sm font-semibold text-gray-800">CRA / Receiver General transactions</h4>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{txns.length}</span>
        </div>
        {txns.length === 0 ? (
          <p className="px-4 py-4 text-sm text-gray-400">
            No CRA payments found. If you remit through a different vendor name, update it in QBO or let us know so we
            can extend the matcher.
          </p>
        ) : (
          <ul>
            {txns.map((tx) => (
              <li key={`${tx.kind}-${tx.id}`} className="border-b border-gray-100 last:border-b-0">
                <button
                  onClick={() => onOpenTxn(tx)}
                  data-testid={`qbo-tax-txn-${tx.id}`}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-emerald-50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] font-semibold text-gray-700">
                      {tx.kind === "bill" ? "Bill" : "Payment"} #{tx.doc_number || tx.id}
                      <span className="ml-2 font-sans text-gray-500">{tx.vendor}</span>
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {tx.txn_date || ""}
                      {tx.due_date ? ` · due ${tx.due_date}` : ""}
                      {tx.payment_type ? ` · ${tx.payment_type}` : ""}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-mono text-sm font-semibold text-gray-800">{money(tx.total)}</p>
                    {Number(tx.balance) > 0 ? (
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-red-600">
                        {money(tx.balance)} owed
                      </p>
                    ) : (
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Paid</p>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Tax agencies */}
      {agencies.length > 0 && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <h4 className="text-sm font-semibold text-gray-800">Tax agencies</h4>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{agencies.length}</span>
          </div>
          <ul>
            {agencies.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-gray-800">{a.name}</p>
                  {a.tax_registration_number && (
                    <p className="truncate font-mono text-[11px] text-gray-400">Reg #{a.tax_registration_number}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="pt-1 text-center text-[11px] text-gray-400">
        Read-only view · figures come directly from QuickBooks Online · liability signs flipped to &quot;amount owed&quot;
      </p>
    </div>
  );
};
